import { Component } from 'preact';
import { motion, AnimatePresence } from 'framer-motion';
import {
  GOLD, ACCENT_GOLD, TEXT_PRIMARY, TEXT_BODY, TEXT_MUTED,
  OPAQUE_TEXTURED_PANEL_STYLE, RADIUS, innerRadius, FourCorners,
  panelCornerStyle, panelCornerGutter, PANEL_CORNER_SIZE, PANEL_CORNER_INSET,
} from '../lib/medievalTheme';
import {
  subscribe as subscribeMoments,
  captureAndPost,
} from '../utils/moments/momentsManager';
import { t } from '../localization/t';

// Moments. Ephemeral social-feed panel docked on the left edge of
// the O dashboard. Mirrors the Mailbox's right-docked geometry
// (380 × 640, vertically centered) so the two feel like a matched
// pair of sidebars.
//
// Always mounted once profile is loaded; visibility is driven by the
// `open` prop so object URLs and scroll position survive toggling.

// Panel dimensions: width as % of canvas-width (cqw), height as % of
// canvas-height (cqh). Matches Mailbox / ChatPanel / CardPickerPanel
// so the social-hub panel family shares a single scale at every canvas.
const PANEL_WIDTH = '23.75cqw';
const PANEL_HEIGHT = '60cqh';
// Same ladder as the Mailbox docked opposite it, so the pair reads as one
// surface split down the screen: floating-popover corner on the panel,
// card corner on the tiles inside its padding.
const PANEL_PADDING = '0.5cqw';
const ROW_PADDING_X = '0.625cqw';

// Captions sit on photographs, so they carry their own contrast the way
// the replay and invite cards' labels do.
const LABEL_SHADOW = '0 1px 2px rgba(0,0,0,0.95), 0 0 6px rgba(0,0,0,0.85)';

function timeAgo(ts) {
  const s = Math.max(1, Math.floor((Date.now() - ts) / 1000));
  if (s < 60) return t('now');
  if (s < 3600) return t('{n}m', { n: Math.floor(s / 60) });
  if (s < 86400) return t('{n}h', { n: Math.floor(s / 3600) });
  return t('{n}d', { n: Math.floor(s / 86400) });
}

// Camera icon for the capture button. Single-stroke outline matches
// the line-icon language used elsewhere (dock controls, mini profile).
const CameraIcon = (props) => (
  <svg style={{ width: '1cqw', height: '1cqw' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <path d="M4 7h3l2-2h6l2 2h3a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V8a1 1 0 0 1 1-1z" />
    <circle cx="12" cy="13" r="4" />
  </svg>
);

export default class MomentsPanel extends Component {
  state = {
    feed: [],
    capturing: false,
    cooldownEndsAt: 0,
    lightbox: null,
  };

  componentDidMount() {
    this.unsub = subscribeMoments((feed) => this.setState({ feed }));
    this.tickTimer = setInterval(() => this.forceUpdate(), 60_000);
  }

  componentWillUnmount() {
    this.unsub?.();
    clearInterval(this.tickTimer);
  }

  onCapture = async () => {
    if (this.state.capturing || Date.now() < this.state.cooldownEndsAt) return;
    this.setState({ capturing: true });
    try {
      await captureAndPost({ selfProfile: this.props.profile });
      // Matches the server rate limit in src/ws/moments.js.
      this.setState({ cooldownEndsAt: Date.now() + 2_000 });
    } catch (err) {
      console.error('[MomentsPanel] capture failed:', err);
      // Surface to the toast layer. The panel itself stays clean.
      if (typeof window !== 'undefined' && window.__valkToast) {
        window.__valkToast(err.message || t('Capture failed'));
      }
    } finally {
      this.setState({ capturing: false });
    }
  };

  openLightbox = (entry) => this.setState({ lightbox: entry });
  closeLightbox = () => this.setState({ lightbox: null });

  render() {
    const { open } = this.props;
    const { feed, capturing, cooldownEndsAt, lightbox } = this.state;
    const cooldownMs = Math.max(0, cooldownEndsAt - Date.now());
    const disabled = capturing || cooldownMs > 0;

    return (
      <>
        {/* Fixed-size panel. Dimensions match Mailbox exactly so the
            two sidebars read as a pair. Stays mounted with pointer
            events off + opacity 0 when closed so scroll position and
            object URLs persist across toggles. */}
        <motion.div
          data-screenshot-ignore="true"
          className="fixed z-[74] flex flex-col"
          animate={{
            opacity: open ? 1 : 0,
            x: open ? 0 : -16,
            y: '-50%',
          }}
          transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
          style={{
            left: '1cqw',
            top: '50%',
            width: PANEL_WIDTH,
            height: PANEL_HEIGHT,
            pointerEvents: open ? 'auto' : 'none',
            // Carved-stone material, opaque base: the panel floats over a
            // live hub screen, so a translucent base would show the board
            // through the stone.
            ...OPAQUE_TEXTURED_PANEL_STYLE,
            borderRadius: RADIUS['2xl'],
            // No top padding: the header takes its own vertical rhythm.
            // Everything else floats on the padding instead of running
            // into the rim.
            padding: `0 ${PANEL_PADDING} ${PANEL_PADDING}`,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <FourCorners color={`${GOLD} 0.55)`} size="1.7cqw" />

          {/* Header: title + capture button. No subtitle. The grid
              below speaks for itself. */}
          <div
            className="shrink-0 flex items-center"
            style={{
              // Tall enough to contain the corner button, so the hairline
              // below lands under it instead of across it.
              height: `calc(${PANEL_CORNER_SIZE} + ${PANEL_CORNER_INSET} * 2)`,
              paddingLeft: ROW_PADDING_X,
              paddingRight: panelCornerGutter(PANEL_PADDING),
              borderBottom: `1px solid ${GOLD} 0.16)`,
            }}
          >
            {/* Title carries the rows' inner inset so it lines up with the
                tiles below, the way the Notifications menu's does. */}
            <span className="text-[0.75cqw] font-bold arena-heading tracking-wide" style={{ color: TEXT_PRIMARY, textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}>
              {t('Moments')}
            </span>
            <button
              type="button"
              disabled={disabled}
              onClick={this.onCapture}
              aria-label={capturing ? t('Capturing') : cooldownMs > 0 ? t('Ready in {n}s', { n: Math.ceil(cooldownMs / 1000) }) : t('Capture')}
              className="vh-invite-btn is-primary flex items-center justify-center"
              // Measured from the panel's corner on both axes, and rounded
              // concentric with it. As a 2.125 x 1.75 box in the header row
              // it was neither square nor equally inset, and its corner was
              // tighter than the panel's.
              style={{ ...panelCornerStyle(), padding: 0 }}
            >
              {cooldownMs > 0 ? (
                <span className="text-[0.625cqw] font-semibold tabular-nums">
                  {Math.ceil(cooldownMs / 1000)}
                </span>
              ) : (
                <CameraIcon />
              )}
            </button>
          </div>

          {/* 2-column grid of tiles. Aspect-ratio 16:9 covers both
              horizontal game captures and UI menus. */}
          <div className="flex-1 overflow-y-auto py-2">
            {feed.length === 0 ? (
              <EmptyState />
            ) : (
              <div className="grid grid-cols-2 gap-2">
                {feed.map((entry, i) => (
                  <MomentTile
                    key={entry.id}
                    entry={entry}
                    index={i}
                    onOpen={() => this.openLightbox(entry)}
                  />
                ))}
              </div>
            )}
          </div>
        </motion.div>

        {/* Fullscreen lightbox as its own fixed overlay. Captured
            moments are only 720 px on their long edge, so leaving the
            <img> at its natural size shows it postage-stamp-small on a
            4K display. The wrapper takes 90 × 88 vh and `object-fit:
            contain` upscales the image to fill whichever dimension
            constrains it, preserving aspect ratio. Gold border and
            rounded corners match the rest of the theme. */}
        <AnimatePresence>
          {lightbox ? (
            <motion.div
              data-screenshot-ignore="true"
              className="fixed inset-0 z-[90] flex items-center justify-center cursor-zoom-out"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.18 }}
              style={{ background: 'rgba(0,0,0,0.86)', backdropFilter: 'blur(8px)' }}
              onClick={this.closeLightbox}
            >
              <motion.div
                className="relative flex items-center justify-center"
                initial={{ scale: 0.92, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.96, opacity: 0 }}
                transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
                style={{
                  width: '90cqw',
                  height: '88cqh',
                  padding: '0.375cqw',
                  borderRadius: RADIUS.xl,
                  background: 'rgba(14,12,8,0.82)',
                  border: `1px solid ${GOLD} 0.35)`,
                  boxShadow: `0 30px 120px rgba(0,0,0,0.85), 0 0 40px ${GOLD} 0.18)`,
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <img
                  src={lightbox.url}
                  alt=""
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    // Concentric with the frame it sits in.
                    borderRadius: innerRadius(RADIUS.xl, '0.375cqw'),
                    display: 'block',
                  }}
                />
                <div
                  className="absolute bottom-3 left-4 right-4 flex items-center justify-between gap-3 text-[0.6875cqw]"
                  style={{ color: TEXT_BODY }}
                >
                  <span className="truncate" style={{ textShadow: '0 2px 6px rgba(0,0,0,0.9)' }}>
                    {lightbox.kind === 'self' ? t('You') : lightbox.authorName}
                  </span>
                  <span style={{ color: ACCENT_GOLD, textShadow: '0 2px 6px rgba(0,0,0,0.9)' }}>
                    {timeAgo(lightbox.createdAt)}
                  </span>
                </div>
              </motion.div>
            </motion.div>
          ) : null}
        </AnimatePresence>
      </>
    );
  }
}

function EmptyState() {
  return (
    <div className="h-full flex flex-col items-center justify-center gap-2 px-6 text-center">
      <div
        className="flex items-center justify-center rounded-full"
        style={{
          width: '4.5cqw',
          height: '4.5cqw',
          background: `${GOLD} 0.06)`,
          boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.08)',
          color: `${GOLD} 0.35)`,
        }}
      >
        <svg style={{ width: '2cqw', height: '2cqw' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
          <path d="M4 7h3l2-2h6l2 2h3a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V8a1 1 0 0 1 1-1z" />
          <circle cx="12" cy="13" r="4" />
        </svg>
      </div>
      <div className="text-[0.75cqw] text-pretty" style={{ color: TEXT_MUTED }}>
        {t('No moments yet. Capture one from the button above.')}
      </div>
    </div>
  );
}

function MomentTile({ entry, index = 0, onOpen }) {
  return (
    <button
      type="button"
      onClick={onOpen}
      // Ring, lift and press live in .vh-moment-tile so they can transition
      // and be interrupted; the old pair of mouseenter handlers wrote the
      // border colour straight onto the node, which could not.
      className="vh-moment-tile vh-social-row-in relative flex w-full text-left cursor-pointer overflow-hidden"
      style={{ aspectRatio: '16 / 9', animationDelay: `${Math.min(index, 9) * 26}ms` }}
    >
      <img
        src={entry.url}
        alt=""
        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
      />
      {/* Overlay strip with author + time. Gradient so the image reads
          even when its bottom edge is busy. */}
      <div
        className="absolute left-0 right-0 bottom-0 flex items-center justify-between gap-1 px-1.5 py-1 text-[0.5625cqw]"
        style={{
          background: 'linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(8,6,4,0.82) 100%)',
          color: TEXT_PRIMARY,
        }}
      >
        <span className="truncate font-semibold" style={{ textShadow: LABEL_SHADOW }}>
          {entry.kind === 'self' ? t('You') : entry.authorName}
        </span>
        <span className="shrink-0 tabular-nums" style={{ color: TEXT_BODY, textShadow: LABEL_SHADOW }}>
          {timeAgo(entry.createdAt)}
        </span>
      </div>
    </button>
  );
}
